import { useEffect } from 'react'
import { useDisclosure, Box, VStack, HStack, Text, GridItem, IconButton } from '@chakra-ui/react'
import DraftIcon from 'assets/icons/draft-dashboard.svg?react'
import { useStores } from 'hooks/useStores'
import isEmpty from 'lodash/isEmpty'
import { observer } from 'mobx-react'
import { useNavigate } from 'react-router-dom'
import ProcessSummary from 'components/Pages/Common/ProcessSummary'
import { getProcessPipeline } from 'components/Pages/ProcessPage/aggregate'
import { queryParser } from 'components/Pages/ProcessPage/aggregate/queryParser'
import SvgIcon from 'components/SvgIcon'
import { AuthRoleIdEnum } from 'constants/user'
import { EIconDefaultId } from 'interfaces/iconBuilder'
import { IProcessWithRelations } from 'interfaces/process'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import routes from 'routes'
import { getValidArray } from 'utils/common'
import { ITheme } from '../../../../interfaces/theme'
import DashboardSectionCard from '../DashboardSectionCard'
import ModalDraftView from '../ModalDraftView'
import styles from './styles.module.scss'

const DraftView = () => {
  const { isOpen: isOpenDashboard, onOpen: onOpenDashboard, onClose: onCloseDashboard } = useDisclosure()
  const { isOpen: isOpenProcessDetail, onOpen: onOpenProcessDetail, onClose: onCloseProcessDetail } = useDisclosure()
  const { processStore, authStore, userStore, organizationStore, groupStore, iconBuilderStore } = useStores()
  const { userDetail } = authStore
  const { currentUser } = userStore
  const isBasicUser = userDetail?.authRoleId === AuthRoleIdEnum.BASIC_USER
  const { organization } = organizationStore
  const currentTheme: ITheme = organization?.theme ?? {}
  const { processes } = processStore
  const navigate = useNavigate()

  const mostRecentProcesses: IProcessWithRelations[] = getValidArray<IProcessWithRelations>(processes).slice(0, 8)

  function handleOpenProcessDetail(processId: number): void {
    processStore.setSelectedProcessId(processId)
    onOpenProcessDetail()
  }

  async function fetchDrafts(organizationId?: number) {
    try {
      if (organizationId && userDetail.id) {
        const dataPipeline = getProcessPipeline(
          organizationId,
          100,
          0,
          [],
          [userDetail.id],
          [],
          [],
          [],
          'updatedAt',
          false,
          false,
          userDetail.id,
          userDetail.authRoleId,
          false,
          '',
          userDetail.authRoleId === AuthRoleIdEnum.BASIC_USER
        )
        await processStore.getProcessByAggregate(dataPipeline, queryParser)
      }
    } catch (error: any) {
      console.log(error)
    }
  }

  useEffect(() => {
    if (!isEmpty(userDetail)) {
      fetchDrafts(organization?.id)
      groupStore.getGroups({ where: { organizationId: organization?.id } })
    }
  }, [organization, userDetail.id])

  if (!currentUser?.isEditor && isBasicUser) return null

  return (
    <>
      <DashboardSectionCard
        icon={<DraftIcon />}
        title="Drafts"
        viewMoreLink={{
          label: 'View all drafts',
          onClick: () => navigate(routes.processes.value)
        }}
        isEmpty={getValidArray(mostRecentProcesses).length === 0}
        emptyTitle="No drafts yet."
        emptyDescription="You don't have any drafts."
        currentTheme={currentTheme}
      >
        <VStack spacing={0} alignItems="flex-start" alignSelf="flex-start" width="full">
          {getValidArray(mostRecentProcesses).map((item: IProcessWithRelations, index: number) => {
            return (
              <GridItem
                width="stretch"
                key={item?.id ?? index}
                gap={0}
                color="gray.700"
                transition="all 0.2s"
                paddingX={4}
                paddingY={3}
                display="flex"
                justifyContent="space-between"
                _hover={{
                  background: 'gray.50',
                  boxShadow: 'sm',
                  color: currentTheme?.primaryColor ?? 'primary.500',
                  transition: 'all 0.2s'
                }}
              >
                <HStack spacing={2} alignItems="flex-start">
                  <Box width="40px" className={styles.icon}>
                    <IconBuilder
                      icon={iconBuilderStore.getIconById(item?.documentType?.iconId ?? EIconDefaultId.DOCUMENT_TYPE)}
                      size={40}
                      isActive
                    />
                  </Box>
                  <Text
                    fontSize="16px"
                    color="inherit"
                    fontWeight="500"
                    lineHeight="24px"
                    marginBottom={0}
                    cursor="pointer"
                    maxHeight="48px"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    alignSelf="center"
                    onClick={() => navigate(routes.processes.processId.value(String(item?.id ?? '')))}
                  >
                    {item?.id ? `${item.id} | ${item?.name ?? ''}` : (item?.name ?? '')}
                  </Text>
                </HStack>
                <Box width="32px" height="32px" display={{ base: 'none', md: 'block' }}>
                  <IconButton
                    backgroundColor="#ffffff"
                    aria-label="Quick View"
                    isRound
                    size="sm"
                    border="none"
                    onClick={() => item?.id && handleOpenProcessDetail(item.id)}
                    icon={<SvgIcon size={16} iconName="ic_detail" />}
                    _hover={{ backgroundColor: '#EDF2F7' }}
                  />
                </Box>
                <Box width="24px" height="24px" display={{ base: 'block', md: 'none' }}>
                  <IconButton
                    backgroundColor="#ffffff"
                    aria-label="Quick View"
                    isRound
                    border="none"
                    size="xs"
                    onClick={() => item?.id && handleOpenProcessDetail(item.id)}
                    icon={<SvgIcon size={20} iconName="ic_detail"></SvgIcon>}
                    _hover={{ backgroundColor: '#EDF2F7' }}
                  />
                </Box>
              </GridItem>
            )
          })}
        </VStack>
      </DashboardSectionCard>
      <ProcessSummary isOpen={isOpenProcessDetail} onClose={onCloseProcessDetail} />
      <ModalDraftView
        isOpen={isOpenDashboard}
        onClose={onCloseDashboard}
        handleOpenProcessDetail={handleOpenProcessDetail}
      />
    </>
  )
}

export default observer(DraftView)
